import React, { useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../snackbarSlice";
import RoundedButton from "../components/RoundedButton";
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Divider,
  Stack,
  InputAdornment,
  Link,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
// useNavigate and useLocation imported above

const ResetPassword = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    const errs = {};
    if (!password) errs.password = "Password is required";
    if (!confirmPassword) errs.confirmPassword = "Confirm your password";
    if (password && confirmPassword && password !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      // Extract token from URL
      const params = new URLSearchParams(location.search);
      const token = params.get("token");
      if (!token) {
        dispatch(
          showSnackbar({
            message: "Invalid or missing reset token.",
            severity: "error",
          })
        );
        setErrors({ ...errs, password: "Invalid or missing reset token." });
        return;
      }
      try {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password`,
          {
            password,
            confirm_password: confirmPassword,
            token,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        setSubmitted(true);
        dispatch(
          showSnackbar({
            message: "Password has been reset successfully!",
            severity: "success",
          })
        );
        setTimeout(() => navigate("/login"), 1500);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          dispatch(
            showSnackbar({
              message: err.response.data.message,
              severity: "error",
            })
          );
          setErrors({ ...errs, password: err.response.data.message });
        } else {
          dispatch(
            showSnackbar({
              message: "Network error. Please try again.",
              severity: "error",
            })
          );
          setErrors({ ...errs, password: "Network error. Please try again." });
        }
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: `url('/login-background.png') no-repeat center center fixed`,
        backgroundSize: "cover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          borderRadius: 2,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 4 },
          maxWidth: 420,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box mb={1} display="flex" alignItems="center" justifyContent="center">
  <img
    src="/austin-logo.png"
    alt="Austin Logo"
    style={{
      width: 200,
      objectFit: "contain"
    }}
  />
  <Typography
    variant="h4"
    component="span"
    fontWeight={700}
    color="#555"
    sx={{ letterSpacing: 1 }}
  >
    QC
  </Typography>
</Box>
        <Divider sx={{ width: "100%", mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} align="center">
          Paperless Quality Control System
        </Typography>
        <Typography variant="body2" fontWeight={500} />
        <Divider sx={{ width: "100%", mt: 1, mb: 2 }} />
        {submitted ? (
          <Typography color="success.main" align="center" sx={{ mb: 2 }}>
            Password has been reset successfully!
          </Typography>
        ) : (
          <>
            <Typography variant="body2" align="center" mb={2}>
              Create a new password to access your account
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((show) => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  autoComplete="new-password"
                />
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowConfirmPassword((show) => !show)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  autoComplete="new-password"
                />
                <RoundedButton sx={{ minHeight: 36, height: 36 }}
                  type="submit"
                  variant="contained"
                  sx={{
                    background: "#a20023",
                    borderRadius: 999,
                    fontWeight: "bold",
                    fontSize: "1.05rem",
                    py: 1.2,
                    mt: 1,
                    "&:hover": { background: "#7a001a" },
                  }}
                  fullWidth
                >
                  RESET PASSWORD
                </RoundedButton>
              </Stack>
            </Box>
          </>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: "100%", mt: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{ color: "#888" }}
            onClick={() => navigate("/login")}
          >
            Back to Login
          </Link>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{ color: "#888" }}
            onClick={() => alert("Terms and Conditions")}
          >
            Terms and Conditions
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
